import { getChatUrl } from "./chatUrl";

/**
 * Which service answers a business's Intelligence chat (docs/ai-platform/09, 09f).
 *
 * The strangler migration (ARCHITECTURE decision 10): the Python agent-brain runs
 * next to `apiChat`, and a business moves over behind a flag. The flag is the
 * Remote Config string `intelligence_chat_brain_businesses`: a comma-separated
 * list of business ids, or `*` for every business. The brain's URL is the
 * build-time `VITE_BRAIN_CHAT_API_URL`; a build without it (prod today) ignores
 * the flag, so flipping the Console value can never point a client at nothing.
 */
export type ChatBackend = "edge" | "brain";

export function parseBrainBusinesses(flag: string | null | undefined): Set<string> {
  return new Set(
    (flag ?? "")
      .split(",")
      .map((id) => id.trim())
      .filter((id) => id.length > 0),
  );
}

export function resolveChatBackend(
  businessId: string | null | undefined,
  flag: string | null | undefined,
  brainUrl: string | null | undefined,
): ChatBackend {
  if (!brainUrl || !businessId) return "edge";
  const ids = parseBrainBusinesses(flag);
  return ids.has("*") || ids.has(businessId) ? "brain" : "edge";
}

export function getBrainChatUrl(): string | undefined {
  const raw = import.meta.env.VITE_BRAIN_CHAT_API_URL as string | undefined;
  const trimmed = raw?.trim().replace(/\/$/, "");
  return trimmed ? trimmed : undefined;
}

/** The chat URL for this business: the brain when flagged in, `apiChat` otherwise. */
export function chatApiUrlFor(
  businessId: string | null | undefined,
  flag: string | null | undefined,
  brainUrl: string | undefined = getBrainChatUrl(),
): { url: string; backend: ChatBackend } {
  const backend = resolveChatBackend(businessId, flag, brainUrl);
  return backend === "brain" && brainUrl
    ? { url: brainUrl, backend }
    : { url: getChatUrl(), backend: "edge" };
}
